import { useEffect, useState } from "react";
import type { PlaybackUrlResponseDto, ViewerVideoDetailDto } from "@madamuh/domain";
import { Link, useNavigate, useParams } from "react-router-dom";
import { createFamilyPlaybackUrl, fetchFamilyVideoDetail } from "../../shared/api/video";
import {
  getProtectedApiErrorMessage,
  isUnauthenticatedApiError
} from "../../shared/session/protectedApiError";
import { DEFAULT_TENANT_TIMEZONE } from "../../shared/lib/tenantDateTime";
import { GENERIC_TENANT_LABELS, getTenantLabels } from "../../shared/lib/tenantLabels";
import { describeStatus } from "../../shared/lib/uiLabels";
import { canUseFamilyMode } from "../../shared/session/modeRouting";
import { useProtectedRouteSession } from "../../shared/session/useProtectedRouteSession";
import { FamilyAppShell } from "../../shared/ui/family/FamilyAppShell";
import { formatFamilyDate, formatFamilyDateTime } from "./familyUi";

export function FamilyVideoDetailPage() {
  const navigate = useNavigate();
  const { tenantSlug = "unknown-tenant", videoId = "" } = useParams();
  const { routeStatus, sessionStatus, session, errorMessage, isSessionReady } = useProtectedRouteSession(tenantSlug);
  const [video, setVideo] = useState<ViewerVideoDetailDto | null>(null);
  const [playback, setPlayback] = useState<PlaybackUrlResponseDto | null>(null);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "error">("idle");
  const [playbackState, setPlaybackState] = useState<"idle" | "loading">("idle");
  const [loadError, setLoadError] = useState<string | null>(null);
  const hasFamilyAccessRole = canUseFamilyMode(session);
  const familyTimeZone = DEFAULT_TENANT_TIMEZONE;
  const tenantLabels = session ? getTenantLabels(session.tenant.uiLabelProfile) : GENERIC_TENANT_LABELS;

  useEffect(() => {
    if (!isSessionReady || !hasFamilyAccessRole || !videoId) {
      return;
    }

    let cancelled = false;

    async function loadVideo() {
      setLoadState("loading");
      setLoadError(null);

      try {
        const nextVideo = await fetchFamilyVideoDetail(tenantSlug, videoId);

        if (cancelled) {
          return;
        }

        setVideo(nextVideo);
        setLoadState("idle");
      } catch (error) {
        if (cancelled) {
          return;
        }

        if (isUnauthenticatedApiError(error)) {
          navigate(`/t/${tenantSlug}/entry`, { replace: true });
          return;
        }

        setLoadState("error");
        setLoadError(getProtectedApiErrorMessage(error, "動画詳細の取得に失敗しました"));
      }
    }

    void loadVideo();

    return () => {
      cancelled = true;
    };
  }, [hasFamilyAccessRole, isSessionReady, navigate, tenantSlug, videoId]);

  async function handlePlayback() {
    if (!videoId) {
      return;
    }

    setPlaybackState("loading");
    setLoadError(null);

    try {
      const nextPlayback = await createFamilyPlaybackUrl(tenantSlug, videoId);
      setPlayback(nextPlayback);
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

        setLoadError(getProtectedApiErrorMessage(error, "動画再生の準備に失敗しました"));
    } finally {
      setPlaybackState("idle");
    }
  }

  return (
    <FamilyAppShell
      tenantSlug={tenantSlug}
      tenantName={session?.tenant.displayName}
      session={session}
      activeTab="communications"
      navigationCountsEnabled={hasFamilyAccessRole}
    >
      <div className="family-page-stack">
        {routeStatus === "checking" || sessionStatus === "loading" ? (
          <article className="family-surface family-notice">
            <p className="family-text-muted">セッションを確認しています。</p>
          </article>
        ) : null}
        {routeStatus === "redirecting" ? (
          <article className="family-surface family-notice">
            <p className="family-text-muted">利用開始へ移動しています。</p>
          </article>
        ) : null}
        {sessionStatus === "error" ? (
          <article className="family-surface family-notice family-notice--warning">
            <p className="family-text-muted">セッション確認に失敗しました。{errorMessage}</p>
          </article>
        ) : null}
        {routeStatus === "authenticated" && !hasFamilyAccessRole ? (
          <article className="family-surface family-notice family-notice--warning">
            <p className="family-text-muted">この画面は保護者または{tenantLabels.student}本人のみ利用できます。</p>
          </article>
        ) : null}

        <section className="family-surface family-section">
          <h2 className="family-page-title">動画詳細</h2>
          <p className="family-page-intro">旧動画ページです。通常の連絡は「連絡」から確認できます。</p>
        </section>

        {loadState === "loading" ? (
          <article className="family-surface family-notice">
            <p className="family-text-muted">動画詳細を読み込んでいます。</p>
          </article>
        ) : null}
        {loadError ? (
          <article className="family-surface family-notice family-notice--warning">
            <p className="family-text-muted">{loadError}</p>
          </article>
        ) : null}

        {video ? (
          <>
            <section className="family-surface family-section" style={{ display: "grid", gap: 8 }}>
              <strong className="family-break-anywhere" style={{ fontSize: 24 }}>{video.title ?? "タイトル未設定"}</strong>
              <div className="family-text-muted">
                {describeStatus(video.videoPurpose)} / {describeStatus(video.targetType)} / {video.targetLabel}
              </div>
              {video.description ? <div className="family-break-anywhere">{video.description}</div> : null}
              <div className="family-text-muted">
                公開期間: {video.publishFrom ? formatFamilyDate(video.publishFrom, familyTimeZone) : "未設定"} 〜 {video.publishTo ? formatFamilyDate(video.publishTo, familyTimeZone) : "未設定"}
              </div>
              <div className="family-text-muted">
                再生可否: {video.playbackAvailable ? "再生できます" : "まだ再生できません"}
              </div>
            </section>

            <section className="family-surface family-section" style={{ display: "grid", gap: 12 }}>
              <button type="button" onClick={() => void handlePlayback()} disabled={playbackState === "loading"}>
                {playbackState === "loading" ? "準備中..." : "動画を再生"}
              </button>

              {playback ? (
                <div className="family-break-anywhere" style={{ display: "grid", gap: 8 }}>
                  <video src={playback.playbackUrl} controls playsInline style={{ width: "100%", borderRadius: 16, background: "#000" }} />
                  <div className="family-text-muted">有効期限: {formatFamilyDateTime(playback.expiresAt, familyTimeZone)}</div>
                </div>
              ) : null}
            </section>
          </>
        ) : null}
      </div>
    </FamilyAppShell>
  );
}
